<template>
  <div class="relative">
    <Listbox v-model="modelValue">
      <div class="relative">
        <ListboxButton
          class="relative w-full cursor-default border bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 sm:text-sm"
          :class="{
            'rounded-lg shadow-sm': !grouped,
            'border-0': grouped,
            'h-14': floating,
            'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
            'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200': readonly
          }"
          :disabled="readonly"
          v-bind="$attrs"
          ref="reference"
          data-testid="listbox-button"
        >
          <template v-if="!!$slots.trigger">
            <slot name="trigger" :selected-option="selectedOption" />
          </template>
          <template v-else>
            <span class="block truncate">{{ selectedOption?.name }}</span>
          </template>
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <YapIcon name="chevron-down-outline" class="h-5 w-5 text-yapgray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="z-40 mt-1 max-h-60 w-80 overflow-auto rounded-lg border bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800 sm:text-sm"
            ref="floating"
            :style="floatingStyles"
          >
            <ListboxOption
              v-slot="{ active, selected }"
              v-for="option in options"
              :key="option.name"
              :value="option.value"
              as="template"
            >
              <li
                :class="[
                  active
                    ? 'bg-primary-50 text-primary-900 dark:bg-primary-dark-950 dark:text-primary-dark-100'
                    : 'text-gray-900 dark:text-primary-dark-100',
                  'relative cursor-default select-none py-2 pl-10 pr-4'
                ]"
              >
                <template v-if="!!$slots.option">
                  <slot name="option" :option="option" :selected="selected" />
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                    <YapIcon
                      name="checkmark-outline"
                      class="h-5 w-5 text-primary-900 dark:text-primary-dark-300"
                      aria-hidden="true"
                    />
                  </span>
                </template>
                <template v-else>
                  <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">{{ option.name }}</span>
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                    <YapIcon name="checkmark-outline" class="h-5 w-5 text-yapgray-400" aria-hidden="true" />
                  </span>
                </template>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>
</template>

<script lang="ts">
export interface YapListBoxOption {
  value: string;
  name?: string;
  extraData?: any;
}
</script>
<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useFloating, autoUpdate, autoPlacement } from '@floating-ui/vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';

import YapIcon from '@/components/YapIcon.vue';

const modelValue = defineModel<string>();

const props = defineProps<{
  options: Array<YapListBoxOption>;
  grouped?: boolean;
  floating?: boolean;
  readonly?: boolean;
}>();

const selectedOption = computed(() => props.options.filter((o) => modelValue.value && o.value === modelValue.value)[0]);

const reference = ref(null);
const floating = ref(null);

const { floatingStyles } = useFloating(reference, floating, {
  whileElementsMounted: autoUpdate,
  middleware: [autoPlacement()]
});
</script>
